import * as React from 'react'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { zustandPlugin } from '@/modules/shared/openreplay'

export type PreviewItem = {
  blobUrl: string
  filename: string
  /** Katalog w paczce ZIP - przy wielu kierowcach jeden folder na kierowce. */
  folder?: string
}

type State = {
  /** Dokumenty w podgladzie - przy wydruku grupowym cala paczka naraz. */
  items: PreviewItem[]
  index: number
}

type Actions = {
  showPreview: (blobUrl: string, filename: string) => void
  /** Otwiera komplet dokumentow w jednym podgladzie, z nawigacja miedzy nimi. */
  showPreviewGroup: (items: PreviewItem[]) => void
  goToPreview: (index: number) => void
  closePreview: () => void
}

const revokeAll = (items: PreviewItem[]) =>
  items.forEach((item) => item.blobUrl && URL.revokeObjectURL(item.blobUrl))

export const usePrintPreviewStore = create<State & Actions>()(
  devtools(
    zustandPlugin((set, get) => ({
      items: [],
      index: 0,

      showPreview: (blobUrl, filename) => {
        const { items } = get()
        // Nowy dokument staje sie biezacym, inaczej doklejony na koniec paczki
        // pozostalby niezauwazony.
        set(
          { items: [...items, { blobUrl, filename }], index: items.length },
          false,
          items.length === 0 ? 'show' : 'append',
        )
      },

      showPreviewGroup: (newItems) => {
        if (newItems.length === 0) return
        revokeAll(get().items)
        set({ items: newItems, index: 0 }, false, 'showGroup')
      },

      goToPreview: (index) => {
        if (index < 0 || index >= get().items.length) return
        set({ index }, false, 'goTo')
      },

      closePreview: () => {
        revokeAll(get().items)
        set({ items: [], index: 0 }, false, 'close')
      },
    }), 'print-preview'),
    { name: 'EasyTacho', store: 'print-preview' },
  ),
)

export type PrintPreviewDerivedState = {
  isOpen: boolean
  blobUrl: string | null
  filename: string | null
  index: number
  count: number
}

export const usePrintPreview = () => {
  const items = usePrintPreviewStore((s) => s.items)
  const index = usePrintPreviewStore((s) => s.index)
  const showPreview = usePrintPreviewStore((s) => s.showPreview)
  const showPreviewGroup = usePrintPreviewStore((s) => s.showPreviewGroup)
  const goToPreview = usePrintPreviewStore((s) => s.goToPreview)
  const closePreview = usePrintPreviewStore((s) => s.closePreview)

  const state = React.useMemo<PrintPreviewDerivedState>(() => {
    const current = items[index] ?? null
    return {
      isOpen: current !== null,
      blobUrl: current?.blobUrl ?? null,
      filename: current?.filename ?? null,
      index,
      count: items.length,
    }
  }, [items, index])

  return {
    state,
    items,
    showPreview,
    showPreviewGroup,
    goToPreview,
    closePreview,
  }
}
